import React, { ReactNode } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  ColumnDef,
  getExpandedRowModel,
} from '@tanstack/react-table';
import { SibTableContainer, Table, useTableSettings } from '@sib/ui';
import { useElementSize } from '@sib/ui/hooks';
import { DEFAULT_PAGE_SIZE } from '../constants';
import { useGetSelectedRowOriginalData } from '../hooks';
import {
  SibTableBody,
  SibTableNoResultsRow,
  SibTableNoResultsRowProps,
} from './body';
import {
  SibTableFooter,
  SibTablePagination,
  SibTablePaginationProps,
} from './footer';
import { SibTableHeader, SibTableSettings } from './header';

interface SibDataTableProps<T> {
  rowCount?: number;
  data: T[];
  columns: ColumnDef<T, unknown>[];
  settings: ReturnType<typeof useTableSettings<T>>;
  minPage?: number;
  loading?: boolean;
  withFixedColumnWidth?: boolean;
  withSettingsRow?: boolean;
  preserveSelectedValues?: boolean;
  withDelayedLoading?: boolean;
  hidePagination?: boolean;
  hideFooter?: boolean;
  noResultsPlaceholder?: ReactNode;
  NoResultsContainerProps?: Partial<SibTableNoResultsRowProps>;
  TableContainerProps?: React.HTMLAttributes<HTMLDivElement>;
  TablePaginationProps?: Partial<SibTablePaginationProps>;
  TableProps?: React.HTMLAttributes<HTMLTableElement>;
}

type WithSubRows<T> = T & { subRows?: T[] };

export const SibDataTable = <T,>({
  settings: {
    tableProps,
    pagination,
    onPaginationChange,
    minPage,
    onOriginalRowSelectionChange,
    disabledColumns,
  },
  rowCount,
  data,
  columns,
  noResultsPlaceholder,
  hideFooter = true,
  hidePagination = false,
  withFixedColumnWidth = false,
  loading = false,
  withSettingsRow = false,
  preserveSelectedValues = false,
  withDelayedLoading = false,
  NoResultsContainerProps,
  TableProps,
  TableContainerProps,
  TablePaginationProps,
}: SibDataTableProps<T>) => {
  const showNoResults = !loading && data.length === 0;
  const { width, ref } = useElementSize<HTMLTableElement>({
    enabled: showNoResults || (loading && withDelayedLoading),
  });

  const getSelectedRowOriginalData = useGetSelectedRowOriginalData({ data });
  const table = useReactTable({
    data,
    columns,
    rowCount,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableExpanding: true,
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: (row: T) => (row as WithSubRows<T>).subRows ?? [],
    manualFiltering: true,
    ...tableProps,
    onRowSelectionChange: updater => {
      tableProps?.onRowSelectionChange?.(updater);
      if (!preserveSelectedValues) return;

      const valuesToCompare =
        typeof updater === 'function'
          ? updater(tableProps.state?.rowSelection || {})
          : updater;

      const originalData = getSelectedRowOriginalData(
        updater,
        valuesToCompare,
        tableProps.state?.originalRowSelection as T[]
      );

      onOriginalRowSelectionChange?.(originalData);
    },
  });

  return (
    <SibTableContainer {...TableContainerProps} ref={ref}>
      {withSettingsRow && (
        <SibTableSettings table={table} disabledColumns={disabledColumns} />
      )}
      <Table
        {...TableProps}
        className="w-max min-w-full border-collapse"
        style={{
          ...(withFixedColumnWidth && {
            tableLayout: 'fixed',
          }),
          ...TableProps?.style,
        }}
        disableWrapper
      >
        <SibTableHeader
          table={table}
          headerGroups={table.getHeaderGroups()}
          hideRounding={withSettingsRow}
        />
        <SibTableBody
          TableDelayedLoadingIndicatorProps={{
            width,
          }}
          withDelayedLoading={withDelayedLoading}
          loading={loading}
          isEmpty={showNoResults}
          pageSize={pagination.pageSize || DEFAULT_PAGE_SIZE}
          table={table}
          emptyPlaceholder={
            <SibTableNoResultsRow
              colSpan={table.getAllColumns().length}
              {...NoResultsContainerProps}
              width={width}
            >
              {noResultsPlaceholder}
            </SibTableNoResultsRow>
          }
        />
        {!hideFooter && <SibTableFooter table={table} />}
      </Table>
      {data.length > 0 && typeof rowCount === 'number' && !hidePagination && (
        <SibTablePagination
          onChange={onPaginationChange}
          rowCount={rowCount}
          state={pagination}
          minPage={minPage}
          {...TablePaginationProps}
        />
      )}
    </SibTableContainer>
  );
};
